import { Button } from "@components/ui/button"
import { Field } from "@components/ui/form/Field"
import { Link } from "@components/ui/link"
import { ModalOrSidebar } from "@components/ui/modal-sidebar"
import { Text } from "@components/ui/typograhpy/Text"
import { CheckoutPlan } from "@generated"
import { downgradeReasons } from "@lib/cancel-reasons"
import { getGraphqlClient } from "@lib/graphql-client"
import { useCurrency } from "@lib/hooks/use-currency"
import { usePlan } from "@lib/hooks/use-plan"
import { extractErrorCode, parseError } from "@lib/parse-error"
import { useDispatch } from "@lib/store"
import { useQueryClient } from "@tanstack/react-query"
import { Form, Formik } from "formik"
import { FC, useState } from "react"

type FormValues = {
  reason: string
}
const { sdk } = getGraphqlClient()

type CancelSubscriptionModalProps = {
  status: "idle" | "disabled"
}

export const DowngradeSubscriptionModal: FC<CancelSubscriptionModalProps> = ({
  status,
}) => {
  const { currency } = useCurrency()
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState<undefined | string>(undefined)
  const queryClient = useQueryClient()
  const { data: planData } = usePlan()

  return (
    <ModalOrSidebar
      open={open}
      onOpenChange={setOpen}
      title="Downgrade plan"
      trigger={
        <Button
          variant="secondary"
          width="fill"
          status={status}
          disabled={planData?.isDowngradedFromBusiness}
        >
          {planData?.isDowngradedFromBusiness
            ? "Pending downgrade"
            : "Downgrade plan"}
        </Button>
      }
    >
      <Text variant="regular" className="mb-6">
        {`If you switch to the basic plan, you will keep all your current credits. Are you sure you want to proceed?`}
      </Text>
      <Formik
        initialValues={{
          reason: "",
        }}
        validateOnChange={true}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form>
            <Text variant="regular" className="mb-4">
              {`Please let us now why you're downgrading.`}
            </Text>
            <Field
              field="select"
              name="reason"
              label="Select reason"
              className="mb-8"
              validation={{
                required: true,
              }}
            >
              <option value="" disabled>
                Choose an option
              </option>
              {downgradeReasons.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Field>
            {error && (
              <Text variant="regular" className="text-danger mb-8">
                {error}
              </Text>
            )}
            <div className="flex items-center justify-between">
              <Link as="button" onClick={() => setOpen((val) => !val)}>
                Cancel
              </Link>
              <Button
                variant="danger"
                status={isLoading ? "loading" : "idle"}
                type="submit"
              >
                Yes, downgrade my plan
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </ModalOrSidebar>
  )

  async function handleSubmit(formValues: FormValues) {
    setLoading(true)

    const matchingReason = downgradeReasons.find(
      (v) => v.value === formValues.reason,
    )

    if (!matchingReason) throw new Error("Unknown reason " + formValues.reason)

    try {
      await sdk.CreateCheckoutSession({
        input: {
          currency,
          cancelUrl: `${window.location.origin}/app?subscription=error`,
          successUrl: `${window.location.origin}/app?subscription=success`,
          plan: CheckoutPlan["BasicPlan_2023V1"],
          downgradeReason: matchingReason.label,
        },
      })
      queryClient.invalidateQueries({ queryKey: ["account"] })

      setOpen(false)
      setError(undefined)
      setOpen(false)
      dispatch({
        type: "add-toast",
        payload: {
          variant: "success",
          title:
            "Successfully downgrade your subscription. You can still see the Business plan until your subscription expires.",
        },
      })
      setOpen(false)
    } catch (error) {
      setError(parseError(extractErrorCode(error)))
      if (process.env.NODE === "development") {
        console.error(error)
      }
    }

    setLoading(false)
  }
}
